import { api } from './client'
import { requireArrayIn } from './readShape'
import { isCapacityMessage, terminalErrorMessage } from '@utils/reportProgress'

/**
 * Report service — wraps cb-api's /reports/* endpoints which proxy CB's
 * legacy ReportsController. The shape returned mirrors CB's Report object
 * (deep-camelized): { columns, reportData, reportFooterCells, reportGroupData,
 * recordCount, totalWidth, ... }.
 */

const reportService = {
  /**
   * GET /reports/list — tabs + reports.
   *
   * **An empty catalog and an unreadable response must never look the same
   * (CC-50321).** This used to be `data?.data?.tabs || []`, which collapsed both
   * into `[]` — so a 200 carrying a body of the wrong shape rendered the Report
   * Center's cheerful "No reports match your search / 0" instead of an error, and
   * nothing anywhere said a read had failed.
   *
   * That is the FOURTH instance of one pattern on this project: the same shape hid
   * CC-49595's group bug through two QA rounds, hid CC-50153's resolver failure, and
   * let the schedules page ship untested. CC-50153 fixed the server half — cb-api
   * now answers 503 rather than 200-with-empty-tabs — and this is the client half of
   * the same honesty.
   *
   * So the shape is validated and a failure THROWS. Every caller reads this through
   * `useQuery` (the catalog, the queue's picker, the schedules picker, the alias
   * settings), so a throw surfaces as that query's `error` state, which is exactly
   * what the callers already render. `tabs: []` is still a perfectly good answer and
   * still returns `[]` — a firm with no reports is a real answer, not a failure.
   *
   * `reports` is validated per tab as well, because `Reports.jsx` does
   * `tabs.flatMap((t) => t.reports.map(...))` — a tab without it does not just
   * render oddly, it throws inside a render.
   *
   * The guard itself moved to `readShape.js` (CC-53052). It was correct but INLINE and
   * unexported, so the next two instances of this same bug — CC-53047's queue and
   * CC-53052's report reads — could only copy it, and spec 087 §5 is explicit that a
   * second copy of a safety check is what this repo must not create. Behaviour here is
   * unchanged; the wording is now shared.
   */
  async list(search = '') {
    const data = await api.get('/reports/list', { search })
    return requireArrayIn(data, 'tabs', {
      noun: 'report catalog', emptyNoun: 'catalog', entryNoun: 'category', childKey: 'reports',
    })
  },

  /** POST /reports/ai-summary — Workers AI plain-language summary of a report.
   *  payload: { name, headers:[], totals:[{label,value}], rows:[[...]], rowCount } */
  async aiSummary(payload) {
    const data = await api.post('/reports/ai-summary', payload)
    return data?.data?.summary || ''
  },

  /** POST /reports/ai-chat — one bounded Q&A turn about a report. Same data
   *  payload as aiSummary plus `messages:[{role,content}]` (the conversation).
   *  Returns { answer, suggestions:[...] } — one AI call per user turn. */
  async aiChat(payload) {
    const data = await api.post('/reports/ai-chat', payload)
    return { answer: data?.data?.answer || '', suggestions: data?.data?.suggestions || [] }
  },

  /** GET /reports/:id/definition — column/filter/group skeleton */
  async getDefinition(reportId) {
    const data = await api.get(`/reports/${reportId}/definition`)
    return data?.data || null
  },

  /** GET /reports/:id/has-chart */
  async hasChart(reportId) {
    const data = await api.get(`/reports/${reportId}/has-chart`)
    return !!data?.data?.hasChart
  },

  /** POST /reports/:id/run — kicks off generation, returns processId */
  async run(reportId, filters = []) {
    const data = await api.post(`/reports/${reportId}/run`, { filters })
    return data?.data || {}
  },

  /** GET /reports/:id/process-status?processId= */
  async getStatus(reportId, processId) {
    const data = await api.get(`/reports/${reportId}/process-status`, { processId })
    return data?.data || { percentComplete: 0, done: false, errored: false }
  },

  /**
   * GET /reports/:id/data?processId=[&jobId=] — full Report.
   *
   * Pass `jobId` whenever the run is one WE stored (a reused job, a poll that just
   * finished, a tray reopen). The route then serves our own R2 copy of that job and
   * never asks CB for the processId file — see the long note on the route (CC-50284).
   * CB discards those files well inside our one-week retention, so asking CB for an
   * older run is a request we expect to fail. `processId` still rides along: it is
   * what the server export needs, and it is the fallback if our copy is gone.
   */
  async getData(reportId, processId, { jobId } = {}) {
    const data = await api.get(`/reports/${reportId}/data`, jobId ? { processId, jobId } : { processId })
    return data?.data || null
  },

  /** POST /reports/:id/data-lite — synchronous, for small reports */
  async getDataLite(reportId, filters = []) {
    const data = await api.post(`/reports/${reportId}/data-lite`, { filters })
    return data?.data || null
  },

  /**
   * Run + poll + fetch in one. Returns the full Report when ready.
   * Calls onProgress(percent, message) during polling.
   */
  async runAndWait(reportId, filters = [], { onProgress, signal, intervalMs = 1500 } = {}) {
    const { processId } = await reportService.run(reportId, filters)
    if (!processId) throw new Error('Report did not return a processId')

    // Bug #6: CB procs that throw report the exception in lastMessage but never
    // move percentComplete to 100/-1 — without a stall guard we polled forever
    // (spinner + disabled Run until a page reload). A run that hasn't STARTED
    // (percent <= 0) gets a short budget; a run in progress gets a longer one
    // so slow-but-alive procs aren't killed.
    let stalledPolls = 0
    let lastSnapshot = null
    while (true) {
      if (signal?.aborted) throw new Error('aborted')
      const status = await reportService.getStatus(reportId, processId)
      onProgress?.(status.percentComplete, status.lastMessage)
      // CC-49982: a terminal failure gets actionable copy — a genuine "too
      // large" result tells the user to narrow the range instead of reading
      // like a crash.
      if (status.errored) throw new Error(terminalErrorMessage(status.lastMessage))
      if (status.done) break
      const snapshot = `${status.percentComplete}|${status.lastMessage || ''}`
      if (snapshot === lastSnapshot) stalledPolls += 1
      else { stalledPolls = 0; lastSnapshot = snapshot }
      // CC-49982: CB posts a "too large" NOTICE at 0% on big-but-healthy runs
      // (report 162, ~33k rows) and then completes. The 0%-start budget (~15s)
      // aborted those before they finished, turning a progress notice into a
      // hard error. A capacity notice earns the in-progress budget (~60s).
      const slowStart = (status.percentComplete ?? 0) <= 0 && !isCapacityMessage(status.lastMessage)
      const budget = slowStart ? 10 : 40 // ~15s / ~60s at 1.5s polls
      if (stalledPolls >= budget) {
        throw new Error(
          terminalErrorMessage(status.lastMessage, 'Report generation stalled — please try again')
        )
      }
      await new Promise((r) => setTimeout(r, intervalMs))
    }
    const report = await reportService.getData(reportId, processId)
    return { report, processId }
  },

  /** Build a download URL for CSV/XLSX export. Browser hits cb-api directly. */
  exportUrl(reportId, format /* 'csv' | 'xlsx' */, { filters = [], processId, rowCount = 0, timeZoneId = '' } = {}) {
    const params = new URLSearchParams()
    params.set('filters', JSON.stringify(filters.map((f) => ({
      ColumnId: f.columnId ?? f.ColumnId,
      Value: f.value ?? f.Value ?? '',
    }))))
    if (processId) params.set('processId', processId)
    if (rowCount) params.set('rowCount', String(rowCount))
    if (timeZoneId) params.set('timeZoneId', timeZoneId)
    return `/api/v1/reports/${reportId}/export.${format}?${params.toString()}`
  },
}

export default reportService
